import React from 'react';
import type { QueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

// import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

import { APIEndPoints, Request } from '../apis';
import type { UserRole } from '../common/types/UserRole';
import { UserResponseSchema } from '../components/types/UserResponse';
import {
  clearAccountStore,
  clearAccountListStore,
  clearApplicationStore,
  clearLoggedInUserStore,
  clearMambuLoanStore,
  clearProductStore,
  clearUsersStore
} from './index';

type UserType =
  | 'platformAdmin'
  | 'platformUser'
  | 'tenantAdmin'
  | 'tenantUser'
  | 'unknown';

type TMerchantServiceUserInfo = {
  userId: string;
  cognitoId: string;
  name: string;
  accountArchived: boolean;
  accountLocked: boolean;
  permissions: {
    readOnlyAccess: boolean;
    paymentAssistAgreement: boolean;
  };
  role: UserRole;
};

type TOriginalUser = {
  merchantId: string;
  userType: UserType;
};

type TUser = {
  merchantId: string;
  userType: UserType;
  sub: string;
  email: string;
};

type TLoginData = {
  email: string;
  session: string;
  challengeName: string;
};

type TSessionData = {
  sessionId?: string;
};

type TSignInResult = {
  AccessToken: string;
  ExpiresIn: number;
  IdToken: string;
  RefreshToken: string;
  TokenType: string;
};

type ChangeUserProps = {
  onUserChange: (updatedUser: TUser) => void;
  setMerchantServiceUserInfo: (userInfo: TMerchantServiceUserInfo) => void;
  user: TUser;
  originalUser: TOriginalUser;
  userInfo: TMerchantServiceUserInfo;
  resetUser: () => void;
  updateLoginData: (newLoginData: TLoginData) => void;
  updateSessionData: (newSessionData: TSessionData) => void;
  resetLoginData: () => void;
  resetStoreData: () => void;
  loginData: TLoginData;
  logOut: () => Promise<boolean>;
  refreshToken: () => void;
};
const NOT_SIGNED_IN = 'NOT_SIGNED_IN';

const defaultOriginalUser: TOriginalUser = {
  merchantId: NOT_SIGNED_IN,
  userType: 'unknown'
};

const defaultUser: TUser = {
  merchantId: NOT_SIGNED_IN,
  userType: 'unknown',
  sub: 'unknown',
  email: 'unknown'
};

const defaultUserInfo: TMerchantServiceUserInfo = {
  userId: NOT_SIGNED_IN,
  cognitoId: NOT_SIGNED_IN,
  name: NOT_SIGNED_IN,
  accountArchived: false,
  accountLocked: false,
  permissions: {
    readOnlyAccess: false,
    paymentAssistAgreement: false
  },
  role: 'Sales'
};

const defaultLoginData: TLoginData = {
  email: '',
  session: '',
  challengeName: ''
};

const defaultSignInResult: TSignInResult = {
  AccessToken: '',
  ExpiresIn: 0,
  IdToken: '',
  RefreshToken: '',
  TokenType: ''
};

const restoredUser: TUser = localStorage.user
  ? JSON.parse(localStorage.user)
  : defaultUser;

const restoredOriginalUser: TOriginalUser = localStorage.originalUser
  ? JSON.parse(localStorage.originalUser)
  : defaultOriginalUser;

const restoredUserInfo: TMerchantServiceUserInfo = localStorage.userInfo
  ? JSON.parse(localStorage.userInfo)
  : defaultUserInfo;

const restoredLoginData: TLoginData = localStorage.loginData
  ? JSON.parse(localStorage.loginData)
  : defaultLoginData;

const defaultUserContext: ChangeUserProps = {
  user: restoredUser,
  originalUser: restoredOriginalUser,
  userInfo: restoredUserInfo,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  onUserChange: (updatedUser: TUser) => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMerchantServiceUserInfo: (userInfo: TMerchantServiceUserInfo) => {},
  // setMerchantServiceUserInfo: (cognitoUserId: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  resetUser: () => {},
  loginData: restoredLoginData,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  updateLoginData: (newLoginData: TLoginData) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
  updateSessionData: (newSessionData: TSessionData) => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  resetLoginData: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  resetStoreData: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  logOut: async () => false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refreshToken: async () => {}
};

const getUserInfo = async (cognitoUserId: string) => {
  const userInfoResponse = await Request.safeGet({
    path: `${APIEndPoints.userInfo}/${cognitoUserId}`,
    schema: UserResponseSchema
  });
  if (userInfoResponse.type === 'right') {
    const {
      id: userId,
      cognitoId,
      name,
      accountArchived = false,
      accountLocked = false,
      permissions: nullablePermissions,
      role
    } = userInfoResponse.value;

    const permissions = {
      readOnlyAccess: false,
      paymentAssistAgreement: false,
      ...nullablePermissions
    };

    const userInfo: TMerchantServiceUserInfo = {
      userId,
      cognitoId,
      name,
      accountArchived,
      accountLocked,
      permissions,
      role
    };
    return userInfo;
  }
  return false;
};

const createPasswordReset = async (email: string) => {
  try {
    const response = await Request.post(
      `${APIEndPoints.passwordReset}`,
      z.any(),
      { username: email }
    );
    return response;
  } catch (error) {
    toast.error(
      'Something went wrong while trying to load this page. Please refresh and try again.'
    );
    throw error;
  }
};

const confirmPasswordReset = async (
  email: string,
  confirmationCode: string,
  newPassword: string
) =>
  Request.put(`${APIEndPoints.passwordReset}`, z.any(), {
    username: email,
    confirmationCode,
    credentials: newPassword
  });

// TODO: this needs to be improved... just here as a
// temporary example for the UI until we work out the roles properly.
const UserContext = React.createContext<ChangeUserProps>(defaultUserContext);

const resetLocalStorageUser = () => {
  localStorage.user = JSON.stringify(defaultUser);
  localStorage.userInfo = JSON.stringify(defaultUserInfo);
  resetLocalStorageOriginalUser();
};
const resetLocalStorageOriginalUser = () => {
  localStorage.originalUser = JSON.stringify(defaultOriginalUser);
};

const resetLocalStorageLoginData = () => {
  localStorage.loginData = JSON.stringify(defaultLoginData);
};

const UserProvider = ({
  queryClient,
  children
}: React.PropsWithChildren<{ queryClient: QueryClient }>) => {
  const [user, setUser] = React.useState<TUser>(restoredUser);
  const [originalUser, setOriginalUser] =
    React.useState<TOriginalUser>(restoredOriginalUser);

  const [userInfo, setUserInfo] =
    React.useState<TMerchantServiceUserInfo>(restoredUserInfo);

  const [loginData, setLoginData] =
    React.useState<TLoginData>(restoredLoginData);

  const [sessionData, setSessionData] = React.useState<TSessionData>({});

  //useInactivityLogout(user);

  // const navigate = useNavigate();
  const logOut = async () => {
    // TODO log
    // console.log('userContext logout');
    // console.log('Full Logout');
    resetLoginData();
    resetUser();
    resetLocalStorageUser();
    resetLocalStorageLoginData();
    resetStoreData();
    // clear any cached query results
    queryClient.clear();
    // this will also clear session data
    localStorage.clear();
    const res = await Request.safeGet({
      path: APIEndPoints.logout,
      schema: z.any()
    });
    return res.type === 'right';
  };

  const refreshOrClearSession = async (): Promise<string | 'SESSION_RESET'> => {
    const accessToken = await Request.safePost(
      APIEndPoints.refreshSession,
      z.string()
    );
    // TODO log
    if (accessToken.type === 'right') {
      //console.log('accessToken success');
      return accessToken.value;
    }
    // TODO log
    //console.log('clearing session: logOut');
    await logOut();
    //await clearSession();
    return 'SESSION_RESET';
  };

  // const clearSession = async () => {
  //   await Request.safeGet({ path: APIEndPoints.logout, schema: z.any() });
  // };

  const refreshToken = async () => {
    // TODO log
    //console.log('refreshToken()');
    const AccessToken = await refreshOrClearSession();

    if (AccessToken === 'SESSION_RESET') {
      // TODO: keep this for testing but remove before MVP
      // TODO log
      //console.log('AccessToken === SESSION_RESET');
      resetLoginData();
      resetUser();
      await logOut();
    } else {
      // TODO log
      //console.log('AccessToken: ', AccessToken);
      // TODO: keep this for testing but remove before MVP
      //console.log('updating user with new token and expiry');
      const updatedUser: TUser = {
        ...user
      };
      const updatedOriginalUser: TOriginalUser = {
        ...user
      };
      onUserChange(updatedUser);
      // onOriginalUserChange(updatedOriginalUser);
    }
  };

  const onUserChange = (user: TUser) => {
    resetLoginData();
    // TODO log
    //console.log('onUserChange', user);
    localStorage.user = JSON.stringify(user);
    setUser(user);
    if (!originalUser || originalUser === defaultOriginalUser) {
      console.log('onUserChange originalUser === defaultOriginalUser');
      onOriginalUserChange({
        merchantId: user.merchantId,
        userType: user.userType
      });
    } else {
      console.log('onUserChange originalUser !== defaultOriginalUser');
    }
  };

  const onOriginalUserChange = (originalUser: TOriginalUser) => {
    resetLoginData();
    localStorage.originalUser = JSON.stringify(originalUser);
    setOriginalUser(originalUser);
  };

  const setMerchantServiceUserInfo = (userInfo: TMerchantServiceUserInfo) => {
    // TODO log
    // console.log('saveMerchantServiceUserInfo');
    setUserInfo(userInfo);
    localStorage.userInfo = JSON.stringify(userInfo);
  };

  const resetUser = () => {
    resetLocalStorageUser();
    setUser(defaultUser);
  };

  const updateLoginData = (newLoginData: TLoginData) => {
    const updatedLoginData: TLoginData = {
      ...loginData,
      ...newLoginData
    };
    resetStoreData();
    localStorage.loginData = JSON.stringify(updatedLoginData);
    setLoginData(updatedLoginData);
  };

  const updateSessionData = (newSessionData: TSessionData) => {
    const updatedData: TLoginData = {
      ...sessionData,
      sessionId: uuidv4(),
      ...newSessionData
    };
    resetStoreData();
    localStorage.session = JSON.stringify(updatedData);
    setSessionData(updatedData);
  };

  const resetLoginData = () => {
    setLoginData(defaultLoginData);
    resetLocalStorageLoginData();
  };

  const resetStoreData = () => {
    clearAccountStore();
    clearAccountListStore();
    clearApplicationStore();
    clearLoggedInUserStore();
    clearMambuLoanStore();
    clearProductStore();
    clearUsersStore();
  };

  const contextValue: ChangeUserProps = {
    onUserChange,
    setMerchantServiceUserInfo,
    user,
    originalUser,
    userInfo,
    resetUser,
    updateLoginData,
    updateSessionData,
    resetLoginData,
    resetStoreData,
    loginData,
    logOut,
    refreshToken
  };
  return (
    <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
  );
};

const isPlatformAdmin = (userType: UserType) => userType === 'platformAdmin';

const isPlatformUser = (userType: UserType) => userType === 'platformUser';

const isTenantAdmin = (userType: UserType) => userType === 'tenantAdmin';

const isTenantUser = (userType: UserType) => userType === 'tenantUser';

const isUnknownUser = (userType: UserType) => userType === 'unknown';

export {
  UserContext,
  UserProvider,
  type UserType,
  type UserRole,
  type TUser,
  type TLoginData,
  type TSignInResult,
  type ChangeUserProps,
  type TMerchantServiceUserInfo,
  confirmPasswordReset,
  createPasswordReset,
  defaultUser,
  defaultLoginData,
  defaultSignInResult,
  defaultUserContext,
  isPlatformAdmin,
  isPlatformUser,
  isTenantAdmin,
  isTenantUser,
  isUnknownUser,
  resetLocalStorageUser,
  resetLocalStorageLoginData,
  getUserInfo,
  NOT_SIGNED_IN
};
